import React, { useState, useEffect } from "react";
import "./JuntaDirectiva.css";
import { Container, Button, Modal, Badge, Spinner } from "react-bootstrap";

const API_URL = "https://facach.coop.hn/api";

// ─── Mensajes por defecto ─────────────────────────────────────────────────────
const MENSAJES_DEFAULT = {
  directiva: [
    "Estimadas cooperativas afiliadas, desde la Junta Directiva reafirmamos nuestro compromiso con el fortalecimiento del movimiento cooperativo y la transparencia institucional.",
    "Seguiremos impulsando iniciativas de educación financiera, transformación digital y buenas prácticas de gobierno cooperativo.",
  ],
  vigilancia: [
    "La Junta de Vigilancia continuará velando por el cumplimiento de las normas y políticas internas, promoviendo la mejora continua y la rendición de cuentas.",
    "Nuestro enfoque es la integridad, la eficiencia de los procesos y la protección de los intereses de las afiliadas.",
  ],
  gerencia: [
    "Desde Gerencia General trabajamos para ejecutar la visión estratégica de FACACH, optimizando recursos y potenciando los servicios a nuestras afiliadas.",
    "Agradecemos la confianza y reiteramos nuestro compromiso con la innovación y la calidad.",
  ],
};

// ─── Jerarquía ────────────────────────────────────────────────────────────────
const JERARQUIA = [
  "presidente", "vice-presidente", "vicepresidente", "secretario",
  "vocalía i", "vocalía ii", "vocalía iii", "vocalía iv", "vocalía v",
  "suplencia",
];
const norm = (s) => (s || "").toString().trim().toLowerCase();
const ordenarPorJerarquia = (miembros = []) =>
  [...miembros].sort((a, b) => {
    const ai = JERARQUIA.indexOf(norm(a.cargo));
    const bi = JERARQUIA.indexOf(norm(b.cargo));
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

// ─── BotonMensaje ─────────────────────────────────────────────────────────────
function BotonMensaje({ label, tituloModal, contenido }) {
  const [show, setShow] = useState(false);
  return (
    <>
      <Button variant="primary" size="sm" className="btn-custom" onClick={() => setShow(true)}>
        {label}
      </Button>
      <Modal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{tituloModal}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {Array.isArray(contenido) ? (
            contenido.map((p, i) =>
              typeof p === "string" && p.includes("<") ? (
                <div key={i} dangerouslySetInnerHTML={{ __html: p }}
                  style={{ textAlign: "justify", marginBottom: 12 }} />
              ) : (
                <p key={i} style={{ textAlign: "justify", marginBottom: 12 }}>{p}</p>
              )
            )
          ) : (
            <p style={{ textAlign: "justify" }}>{contenido}</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

// ─── StepItem ─────────────────────────────────────────────────────────────────
function StepItem({ cargo, nombre, cooperative_name, zone_name, mensajeContenido, esUltimo = false, esGerencia = false }) {
  const mostrarMensaje = esGerencia
    ? norm(cargo) === "gerente general"
    : norm(cargo) === "presidente";

  return (
    <li className={`step-item ${esUltimo ? "last" : ""}`}>
      <div className="step-marker" aria-hidden />
      <div className="step-content">
        <div className="step-header">
          <span className="chip-cargo" title={cargo}>{cargo}</span>
          <div className="acciones-inline">
            {mostrarMensaje && mensajeContenido && (
              <BotonMensaje
                label="Ver mensaje"
                tituloModal={`Mensaje — ${cargo}`}
                contenido={mensajeContenido}
              />
            )}
          </div>
        </div>

        <div className="step-body">
          <div className="nombre" title={nombre}>{nombre}</div>
          {cooperative_name && (
            <div className="cooperativa text-muted">{cooperative_name}</div>
          )}
          {zone_name && (
            <div className="zona-badge">
              <span className="zona-chip">{zone_name}</span>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

// ─── SeccionLista ─────────────────────────────────────────────────────────────
function SeccionLista({ titulo, miembros, mensajeContenido }) {
  const propietarios = ordenarPorJerarquia(
    miembros.filter(m => norm(m.tipo) === "propietario")
  );
  const suplentes = ordenarPorJerarquia(
    miembros.filter(m => norm(m.tipo) === "suplente")
  );

  return (
    <>
      <h3 className="titulo-seccion">{titulo}</h3>

      {propietarios.length > 0 && (
        <>
          <div className="subtitulo">Miembros Propietarios</div>
          <ul className="stepped-list">
            {propietarios.map((m, i) => (
              <StepItem
                key={`prop-${m.id}`}
                {...m}
                esUltimo={i === propietarios.length - 1}
                mensajeContenido={mensajeContenido}
              />
            ))}
          </ul>
        </>
      )}

      {suplentes.length > 0 && (
        <>
          <div className="subtitulo mt-4">Miembros Suplentes</div>
          <ul className="stepped-list">
            {suplentes.map((m, i) => (
              <StepItem
                key={`sup-${m.id}`}
                {...m}
                esUltimo={i === suplentes.length - 1}
                mensajeContenido={mensajeContenido}
              />
            ))}
          </ul>
        </>
      )}
    </>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────
export default function JuntaDirectiva({ maxWidth = 1100, mensajes = MENSAJES_DEFAULT }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch_ = async () => {
      try {
        const res  = await fetch(`${API_URL}/board-members`);
        const json = await res.json();
        if (json.ok) setData(json.grouped);
      } catch (err) {
        console.error("Error cargando junta directiva:", err.message);
      } finally { setLoading(false); }
    };
    fetch_();
  }, []);

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  const directiva  = data?.junta_directiva  || [];
  const vigilancia = data?.junta_vigilancia || [];
  const gerencia   = data?.gerencia_general || [];
  const jefaturas  = data?.jefaturas        || [];

  return (
    <section data-bs-theme="light" className="juntas-wrap">
      <Container className="my-5" style={{ maxWidth }}>

        {/* Junta Directiva */}
        <SeccionLista
          titulo="Junta Directiva"
          miembros={directiva}
          mensajeContenido={mensajes.directiva}
        />

        {/* Junta de Vigilancia */}
        <SeccionLista
          titulo="Junta de Vigilancia"
          miembros={vigilancia}
          mensajeContenido={mensajes.vigilancia}
        />

        {/* Gerencia General */}
        {gerencia.length > 0 && (
          <>
            <h3 className="titulo-seccion mt-5">Gerencia General</h3>
            <ul className="stepped-list">
              {gerencia.map((m, i) => (
                <StepItem
                  key={`ger-${m.id}`}
                  {...m}
                  esUltimo={i === gerencia.length - 1}
                  mensajeContenido={mensajes.gerencia}
                  esGerencia
                />
              ))}
            </ul>
          </>
        )}

        {/* Jefaturas FACACH */}
        {jefaturas.length > 0 && (
          <>
            <h3 className="titulo-seccion mt-5">Jefaturas FACACH</h3>
            <ul className="stepped-list">
              {jefaturas.map((m, i) => (
                <StepItem
                  key={`jef-${m.id}`}
                  {...m}
                  esUltimo={i === jefaturas.length - 1}
                  mensajeContenido={null}
                  esGerencia={false}
                />
              ))}
            </ul>
          </>
        )}

      </Container>
    </section>
  );
} 